import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Table, TableBody, TableCell, TableHead,
    TableRow, Tooltip, Typography,
} from '@mui/material';
import { usePrikazyKanaluQuery } from '../../api/JsdhNotifikaceService/service/IotZarizeniMutations';
import type { IotKanal } from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import { formatDatumCas, formatPlnyCas } from './iotStav';

export const PRIKAZ_STAV: Record<string, string> = {
    POTVRZENO: 'potvrzeno', PRESKOCENO: 'přeskočeno', NEODPOVEDELO: 'neodpovědělo', CHYBA: 'chyba', ODESLANO: 'odesláno',
    ODMITNUTO: 'odmítnuto', GATEWAY_NEDOSTUPNA: 'server nedostupný',
};
/** Výsledky, které se zvýrazní warning textem. */
const PRIKAZ_STAV_VAROVANI = new Set(['NEODPOVEDELO', 'CHYBA', 'ODMITNUTO', 'GATEWAY_NEDOSTUPNA']);

const PRIKAZ_AKCE: Record<string, string> = {
    ZAPNOUT: 'zapnout', VYPNOUT: 'vypnout', OBNOVIT: 'vrácení stavu po poplachu', COVER_OTEVRIT: 'otevřít', COVER_ZAVRIT: 'zavřít',
    COVER_STOP: 'zastavit', COVER_POZICE: 'nastavit polohu na', LIGHT_NASTAVIT: 'rozsvítit na',
};

/** Příkaz slovy: „zapnout na 30 min“, „puls 2 s“, „rozsvítit na 60 %“, „vrácení stavu po poplachu“. */
export const popisAkcePrikazu = (p: Record<string, unknown>): string => {
    const akce = String(p.akce ?? '');
    const t = p.toggleAfterS as number | null | undefined;
    const hodnota = p.cilovaHodnota as number | null | undefined;
    // Gateway vraci puls i zapnuti na minuty jako ZAPNOUT s toggleAfterS; rozlisit lze jen podle delky:
    // cele minuty = zapnuti na minuty (puls delky N*60 s vypada stejne a ma i stejny ucinek).
    if (akce === 'ZAPNOUT' && t) return t % 60 === 0 ? `zapnout na ${t / 60} min` : `puls ${t} s`;
    const text = PRIKAZ_AKCE[akce] ?? akce.toLowerCase();
    return hodnota != null && akce !== 'VYPNOUT' && akce !== 'OBNOVIT' ? `${text} ${hodnota} %` : text;
};

/** Čas v historii: „dnes 0:14“, „včera 22:10“, jinak „5. 10. 23:51“. */
export const formatCasPrikazu = (ms: number, now: number = Date.now()): string => {
    const d = new Date(ms);
    const ted = new Date(now);
    const hm = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
    const den = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
    if (den(d) === den(ted)) return `dnes ${hm}`;
    if (den(d) === den(new Date(ted.getFullYear(), ted.getMonth(), ted.getDate() - 1))) return `včera ${hm}`;
    return formatDatumCas(ms, now);
};

/** Kdo příkaz spustil: poplach (odkaz na detail události), uživatel u ručního příkazu, jinak „po poplachu“. */
const Spustil: React.FC<{ p: Record<string, unknown> }> = ({ p }) => {
    const poplachId = p.poplachId != null ? String(p.poplachId) : null;
    if (p.zdroj === 'RUCNE') return <>{p.uzivatel ? String(p.uzivatel) : 'ručně'}</>;
    if (poplachId) return <Link component={RouterLink} to={`/admin/udalosti/${poplachId}`}>poplach č. {poplachId}</Link>;
    return <>{p.zdroj === 'POPLACH' ? 'poplach' : p.zdroj ? 'po poplachu' : ''}</>;
};

const LIMIT = 10;

const HistoriePrikazuDialog: React.FC<{ zarizeniId: number; kanal: IotKanal; onClose: () => void }> = ({ zarizeniId, kanal, onClose }) => {
    const { data: prikazy = [], isLoading, isError } = usePrikazyKanaluQuery(zarizeniId, kanal.id, true);
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Historie příkazů: {kanal.nazev}</DialogTitle>
            <DialogContent>
                {isLoading && <CircularProgress size={24} />}
                {isError && <Alert severity="error">Historii příkazů se nepodařilo načíst.</Alert>}
                {!isLoading && !isError && prikazy.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Výstup zatím nedostal žádný příkaz.</Typography>
                )}
                {prikazy.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow><TableCell>Čas</TableCell><TableCell>Příkaz</TableCell><TableCell>Spustil</TableCell><TableCell>Výsledek</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                            {prikazy.map((p, i) => {
                                const ms = Number(p.odeslanoEpochMs);
                                const stav = String(p.stav ?? '');
                                const vysledek = PRIKAZ_STAV[stav] ?? stav;
                                return (
                                    <TableRow key={p.prikazId != null ? String(p.prikazId) : i}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {Number.isFinite(ms) && ms > 0 ? <Tooltip title={formatPlnyCas(ms)}><span>{formatCasPrikazu(ms)}</span></Tooltip> : ''}
                                        </TableCell>
                                        <TableCell>{popisAkcePrikazu(p)}</TableCell>
                                        <TableCell><Spustil p={p} /></TableCell>
                                        <TableCell>
                                            <Tooltip title={p.poznamka ? String(p.poznamka) : ''}>
                                                <Typography variant="body2" component="span" color={PRIKAZ_STAV_VAROVANI.has(stav) ? 'warning.main' : 'text.primary'}>
                                                    {vysledek}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
                {prikazy.length >= LIMIT && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Zobrazeno posledních {LIMIT} příkazů.</Typography>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Zavřít</Button></DialogActions>
        </Dialog>
    );
};

export default HistoriePrikazuDialog;
